import React, { useEffect, useState, useRef, useMemo } from "react";
import axios from "axios";
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line,
  Area, AreaChart, Sector
} from "recharts";
import { BarChart2, CheckCircle, Clock, Wallet, XCircle } from "lucide-react";
import LoadingScreen from "../components/LoadingScreen";
import { toast } from 'sonner';

const RebateViewer = () => {
  // ======= STATE MANAGEMENT =======
  const [rebates, setRebates] = useState([]);
  const [filteredRebates, setFilteredRebates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoaded, setIsLoaded] = useState(false);
  const [animationStage, setAnimationStage] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState("all");
  const [showFilters, setShowFilters] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [activeChartType, setActiveChartType] = useState("pie");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [visibleColumns, setVisibleColumns] = useState({});
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [savedViews, setSavedViews] = useState([]);
  const [viewName, setViewName] = useState("");
  const [showSaveViewModal, setShowSaveViewModal] = useState(false);
  const [showHelpTooltip, setShowHelpTooltip] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [trendTimespan, setTrendTimespan] = useState("week");
  const [highlightedRow, setHighlightedRow] = useState(null);

  // Filter states
  const [teamLeaderFilter, setTeamLeaderFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [lobFilter, setLobFilter] = useState("");
  // Add these state variables alongside your other state variables
  const [trendChartPage, setTrendChartPage] = useState(1);
  const [trendChartItemsPerPage, setTrendChartItemsPerPage] = useState(10);
  // References
  const tableRef = useRef(null);
  // Add these state variables to your component
  const [tlChartPage, setTlChartPage] = useState(1);
  const [tlChartItemsPerPage, setTlChartItemsPerPage] = useState(10);
  const [checkedRows, setCheckedRows] = useState({});
  const [allChecked, setAllChecked] = useState(false);
  // ======= INITIALIZATIONS & DATA FETCHING =======
  const updateSelectAllCheckboxState = (checkedRowsState = checkedRows) => {
    const eligibleRows = currentRecords.filter(doc =>
      doc["TL Status"] === null ||
      (doc["TL Status"] || "").toLowerCase().trim() === "for approval" ||
      (doc["TL Status"] || "").toLowerCase().trim() === "pending" ||
      (doc["TL Status"] || "").toLowerCase().trim() === ""
    );

    const allEligibleChecked = eligibleRows.length > 0 && eligibleRows.every((doc, i) => {
      const rowIndex = indexOfFirstRecord + currentRecords.indexOf(doc);
      return checkedRowsState[rowIndex];
    });

    setAllChecked(allEligibleChecked);
  };
  // Initial column visibility setup
  useEffect(() => {
    if (rebates.length > 0 && Object.keys(visibleColumns).length === 0) {
      const columns = {};
      Object.keys(rebates[0])
        .filter(key => key !== "_id")
        .forEach(key => {
          columns[key] = true;
        });
      setVisibleColumns(columns);
    }
  }, [rebates]);

  // Enhanced entrance animation sequence
  useEffect(() => {
    setIsLoaded(false);
    setAnimationStage(0);

    const stageTimers = [
      setTimeout(() => {
        setIsLoaded(true);
      }, 100),
      setTimeout(() => {
        setAnimationStage(1);
      }, 300),
      setTimeout(() => {
        setAnimationStage(2);
      }, 600),
      setTimeout(() => {
        setAnimationStage(3);
      }, 900),
      setTimeout(() => {
        setAnimationStage(4);
      }, 1200)
    ];

    return () => stageTimers.forEach(timer => clearTimeout(timer));
  }, []);

  // Fetch rebate data
  // Modify your useEffect for fetching data
  useEffect(() => {
    const fetchRebates = async () => {
      console.log("⏳ Fetching rebate data...");

      try {
        const res = await axios.get("http://localhost:5000/api/rebate/supervisor-rebate-data");
        console.log("✅ Rebate API response:", res.data);

        if (res.data.success) {
          const data = res.data.data || [];
          setRebates(data);
          setFilteredRebates(data); // default: all shown

          // Apply tab filter if active
          if (activeTab !== "all") {
            applyTabFilter(activeTab, data);
          }
        } else {
          console.error("Failed to fetch rebate data.");
          setRebates([]);
        }
      } catch (error) {
        console.error("❌ Fetch error:", error.response?.data || error.message);
        setRebates([]);
      } finally {
        // End loading state, but don't show content yet
        setLoading(false);

        // Start animation sequence after loading is complete
        startAnimationSequence();
      }
    };

    // Function to handle animation sequence
    const startAnimationSequence = () => {
      // First set isLoaded to true to begin animations
      setIsLoaded(true);

      // Then gradually increment animation stage for sequential animations
      let stage = 0;
      const interval = setInterval(() => {
        stage++;
        setAnimationStage(stage);

        if (stage >= 5) { // Assuming 5 animation stages
          clearInterval(interval);
        }
      }, 150); // 150ms between animation stages
    };

    fetchRebates();

    // Load saved views from localStorage
    const savedViewsFromStorage = localStorage.getItem('rebateViews');
    if (savedViewsFromStorage) {
      setSavedViews(JSON.parse(savedViewsFromStorage));
    }
  }, []);


  // Apply tab filter when tab changes
  useEffect(() => {
    if (rebates.length > 0) {
      applyTabFilter(activeTab, rebates);
    }
  }, [activeTab]);

  // ======= DATA TRANSFORMATIONS & UTILITIES =======

  // Clean values for display
  const cleanValue = (val) => {
    if (typeof val === "string") {
      return val.replace(/<br\s*\/?>/gi, ", ").replace(/\n/g, "").trim();
    }
    return val ?? "";
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Time-related utility functions
  const getDateDaysAgo = (days) => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date;
  };

  // Group rebates by date for trend data
  const getTrendData = () => {
    const today = new Date();
    let startDate;
    let dateFormat;
    let groupBy;

    if (trendTimespan === "week") {
      startDate = getDateDaysAgo(7);
      dateFormat = { weekday: 'short' };
      groupBy = (date) => date.toLocaleDateString('en-US', { weekday: 'short' });
    } else if (trendTimespan === "month") {
      startDate = getDateDaysAgo(30);
      dateFormat = { day: '2-digit' };
      groupBy = (date) => date.getDate();
    } else if (trendTimespan === "quarter") {
      startDate = getDateDaysAgo(90);
      dateFormat = { month: 'short' };
      groupBy = (date) => date.toLocaleDateString('en-US', { month: 'short' });
    }

    const groups = {};

    // Initialize all dates in range
    if (trendTimespan === "week") {
      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const todayIdx = today.getDay();

      for (let i = 6; i >= 0; i--) {
        const idx = (todayIdx - i + 7) % 7;
        groups[days[idx]] = {
          count: 0,
          amount: 0,
          pending: 0
        };
      }
    } else if (trendTimespan === "month") {
      for (let i = 29; i >= 0; i--) {
        const date = getDateDaysAgo(i);
        groups[date.getDate()] = {
          count: 0,
          amount: 0,
          pending: 0
        };
      }
    } else if (trendTimespan === "quarter") {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const currentMonthIdx = today.getMonth();

      for (let i = 2; i >= 0; i--) {
        const idx = (currentMonthIdx - i + 12) % 12;
        groups[months[idx]] = {
          count: 0,
          amount: 0,
          pending: 0
        };
      }
    }

    // Count rebates by date
    filteredRebates.forEach(rebate => {
      const dateStr = rebate["Date Today"];
      if (!dateStr) return;

      const date = new Date(dateStr);
      if (isNaN(date.getTime()) || date < startDate) return;

      const key = groupBy(date);

      if (!groups[key]) {
        groups[key] = {
          count: 0,
          amount: 0,
          pending: 0
        };
      }

      groups[key].count++;

      // Add amount
      let amtStr = rebate["Estimated Rebate Amount"] || "0";
      if (typeof amtStr === "string") {
        amtStr = amtStr.replace(/\n/g, "").trim();
      }
      const amt = parseFloat(amtStr);
      if (!isNaN(amt)) {
        groups[key].amount += amt;
      }

      // Check if pending
      const status = (rebate["TL Status"] || "").toLowerCase().trim();
      if (rebate["TL Status"] === null || status === "for approval") {
        groups[key].pending++;
      }
    });

    // Convert to array for Recharts
    return Object.entries(groups).map(([date, stats]) => ({
      date,
      total: stats.count,
      amount: stats.amount,
      pending: stats.pending
    }));
  };
  const getAgingDualViewData = () => {
    // Filter only pending rebates
    const pendingRebates = filteredRebates.filter(doc => {
      const status = (doc["TL Status"] || "").toLowerCase().trim();
      return doc["TL Status"] === null || status === "for approval" || status === "pending" || status === "";
    });

    // Calculate aging days for each rebate
    const agingData = pendingRebates.map(rebate => {
      const agingDays = calculateAgingDays(rebate["Date Today"]);
      const category = getAgingCategory(agingDays);

      return {
        ...rebate,
        agingDays,
        category,
        ticketNumber: rebate["Ticket Number"] || "Unknown",
        agentName: rebate["Agent Name"] || "Unknown",
        rebateAmount: parseFloat((rebate["Rebate Amount"] || "0").toString().replace(/\n/g, "").trim()) || 0
      };
    });

    // Sort by aging days (descending)
    const sortedData = agingData.sort((a, b) => b.agingDays - a.agingDays);

    // Get top oldest rebates for the table
    const topOldestRebates = sortedData.slice(0, 10);

    // Group data by aging category for the pie chart
    const categoryData = {};
    // Initialize counters for each category to ensure all categories are represented
    ["1-7 days", "8-14 days", "15-30 days", "31-60 days", "60+ days"].forEach(cat => {
      categoryData[cat] = {
        name: cat,
        value: 0,
        amount: 0,
        color: getAgingColor(cat)
      };
    });

    // Count rebates in each category
    agingData.forEach(rebate => {
      categoryData[rebate.category].value++;
      categoryData[rebate.category].amount += rebate.rebateAmount;
    });

    // Convert to array for chart display (in order from oldest to newest)
    const pieData = ["60+ days", "31-60 days", "15-30 days", "8-14 days", "1-7 days"]
      .filter(category => categoryData[category].value > 0) // Only include categories with values
      .map(category => categoryData[category]);

    return {
      pieData,
      tableData: sortedData, // Return ALL data for the table (instead of just topOldestRebates)
      totalPending: agingData.length,
      totalAmount: agingData.reduce((sum, item) => sum + item.rebateAmount, 0)
    };
  };

  // Get derived totals (full dataset)
  const totalRebates = filteredRebates.length;

  const totalAmount = useMemo(() => {
    return filteredRebates.reduce((sum, doc) => {
      let amtStr = doc["Estimated Rebate Amount"] || "0";
      if (typeof amtStr === "string") {
        amtStr = amtStr
          .replace(/\n/g, "")
          .trim();
      }
      const amt = parseFloat(amtStr);
      return sum + (isNaN(amt) ? 0 : amt);
    }, 0);
  }, [filteredRebates]);

  const pendingCount = useMemo(() => {
    return filteredRebates.filter(doc => {
      const status = (doc["TL Status"] || "").toLowerCase().trim();
      return status === "pending" || status === "for approval" || status === "";
    }).length;
  }, [filteredRebates]);
  

  const approvedCount = useMemo(() => {
    return filteredRebates.filter(doc => {
      const status = (doc["TL Status"] || "").toLowerCase().trim();
      return status === "approved";
    }).length;
  }, [filteredRebates]);

  const rejectedCount = useMemo(() => {
    return filteredRebates.filter(doc => {
      const status = (doc["TL Status"] || "").toLowerCase().trim();
      return status === "cancelled";  // <-- updated from 'rejected'
    }).length;
  }, [filteredRebates]);


  // Get list of team leaders, statuses, and line of business options
  const teamLeaders = Array.from(new Set(rebates.map(doc => doc["Team Leader"]?.trim() || "Unknown")));
  const statuses = Array.from(
    new Set(
      rebates.map(doc => (doc["TL Status"] || "").toLowerCase().trim() || "pending")
    )
  );
    const lobList = Array.from(new Set(rebates.map(doc => (doc["Line of Business"] || "Unknown").trim())));

  // ======= FILTERING & SORTING FUNCTIONS =======

  // Filter rebates based on active tab
  const applyTabFilter = (tab, data) => {
    if (tab === "all") {
      setFilteredRebates(data);
    } else if (tab === "pending") {
      const filtered = data.filter(doc => {
        const status = (doc["TL Status"] || "").toLowerCase().trim();
        return (
          status === "for approval" ||
          status === "pending" ||
          status === null ||
          status === ""
        );
      });
      setFilteredRebates(filtered);
    } else if (tab === "approved") {
      const filtered = data.filter(doc => {
        const status = (doc["TL Status"] || "").toLowerCase().trim();
        return status === "approved";
      });
      setFilteredRebates(filtered);
    } else if (tab === "rejected") {
      const filtered = data.filter(doc => {
        const status = (doc["TL Status"] || "").toLowerCase().trim();
        return status === "cancelled";  // <-- updated from 'rejected'
      });
      setFilteredRebates(filtered);
    }

    setCurrentPage(1);
  };


  // Step 2: Modify the handleFilter function to handle "Pending" selection specially

  const handleFilter = () => {
    let filtered = [...rebates];

    if (teamLeaderFilter) {
      filtered = filtered.filter(doc =>
        (doc["Team Leader"] || "").toLowerCase().trim() === teamLeaderFilter.toLowerCase()
      );
    }

    if (statusFilter) {
        const normalizedStatus = statusFilter.toLowerCase().trim();
      
        if (normalizedStatus === "pending") {
          filtered = filtered.filter(doc => {
            const status = (doc["TL Status"] || "").toLowerCase().trim();
            return status === "pending" || status === "for approval" || status === "";
          });
        } else {
          filtered = filtered.filter(doc => {
            const status = (doc["TL Status"] || "").toLowerCase().trim();
            return status === normalizedStatus;
          });
        }
      }
      

    // Rest of filtering logic remains the same
    if (dateFrom) {
      filtered = filtered.filter(doc => {
        const dateValue = new Date(doc["Date Today"]);
        return dateValue >= new Date(dateFrom);
      });
    }

    if (dateTo) {
      filtered = filtered.filter(doc => {
        const dateValue = new Date(doc["Date Today"]);
        return dateValue <= new Date(dateTo);
      });
    }

    if (lobFilter) {
      filtered = filtered.filter(doc =>
        (doc["Line of Business"] || "")
          .replace(/\n/g, "")
          .trim()
          .toLowerCase() === lobFilter.toLowerCase()
      );
    }

    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(doc => {
        return Object.entries(doc).some(([key, value]) => {
          if (key === "_id") return false;
          const stringValue = String(value || "").toLowerCase();
          return stringValue.includes(searchLower);
        });
      });
    }

    setFilteredRebates(filtered);
    setCurrentPage(1);
  };

  // Quick filter presets
  // Updated handleQuickFilter function with reset case
  const handleQuickFilter = (filterType) => {
    // Reset all filters first
    setTeamLeaderFilter("");
    setStatusFilter("");
    setDateFrom("");
    setDateTo("");
    setLobFilter("");
    setSearchText("");

    // If it's reset type, just execute the filter with all reset values
    if (filterType === "reset") {
      setTimeout(() => handleFilter(), 0);
      return;
    }

    // Apply specific quick filter
    if (filterType === "pendingApproval") {
      setStatusFilter("for approval");
    } else if (filterType === "approvedToday") {
      setStatusFilter("approved");
      const today = new Date().toISOString().split('T')[0];
      setDateFrom(today);
      setDateTo(today);
    } else if (filterType === "rejectedToday") {
      setStatusFilter("rejected");
      const today = new Date().toISOString().split('T')[0];
      setDateFrom(today);
      setDateTo(today);
    } else if (filterType === "thisWeek") {
      const today = new Date();
      const dayOfWeek = today.getDay();
      const startDate = new Date(today);
      startDate.setDate(today.getDate() - dayOfWeek);
      setDateFrom(startDate.toISOString().split('T')[0]);
      setDateTo(today.toISOString().split('T')[0]);
    } else if (filterType === "thisMonth") {
      const today = new Date();
      const startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateFrom(startDate.toISOString().split('T')[0]);
      setDateTo(today.toISOString().split('T')[0]);
    }

    // Execute filter after setting the values
    setTimeout(() => handleFilter(), 0);
  };

  // Reset button component
  <button
    className="px-3 py-1.5 text-xs font-medium rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200"
    onClick={() => handleQuickFilter('reset')}
    aria-label="Reset all filters"
  >
    <span className="flex items-center">
      <XCircle className="w-3 h-3 mr-1" />
      Reset All
    </span>
  </button>

  // Clear all filters
  const clearAllFilters = () => {
    setTeamLeaderFilter("");
    setStatusFilter("");
    setDateFrom("");
    setDateTo("");
    setLobFilter("");
    setSearchText("");
    setFilteredRebates(rebates);
    setCurrentPage(1);
  };

  // Sort table columns
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    } else if (sortConfig.key === key && sortConfig.direction === 'descending') {
      key = null;
    }

    setSortConfig({ key, direction });
  };

  // Apply sorting to data
  const sortedData = useMemo(() => {
    if (!sortConfig.key) return filteredRebates;

    return [...filteredRebates].sort((a, b) => {
      const keyA = a[sortConfig.key] || '';
      const keyB = b[sortConfig.key] || '';

      // Date sorting
      if (sortConfig.key === "Date Today") {
        const dateA = new Date(keyA);
        const dateB = new Date(keyB);

        if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
          return String(keyA).localeCompare(String(keyB)) * (sortConfig.direction === 'ascending' ? 1 : -1);
        }

        return (dateA.getTime() - dateB.getTime()) * (sortConfig.direction === 'ascending' ? 1 : -1);
      }

      // Numeric sorting
      if (sortConfig.key === "Rebate Amount") {
        const numA = parseFloat(String(keyA).replace(/\n/g, "").trim()) || 0;
        const numB = parseFloat(String(keyB).replace(/\n/g, "").trim()) || 0;
        return (numA - numB) * (sortConfig.direction === 'ascending' ? 1 : -1);
      }

      // Default string sorting
      return String(keyA).localeCompare(String(keyB)) * (sortConfig.direction === 'ascending' ? 1 : -1);
    });
  }, [filteredRebates, sortConfig]);

  // Save current view
  const saveCurrentView = () => {
    if (!viewName) return;

    const view = {
      id: Date.now(),
      name: viewName,
      filters: {
        teamLeader: teamLeaderFilter,
        status: statusFilter,
        dateFrom,
        dateTo,
        lob: lobFilter,
        searchText
      },
      sort: sortConfig,
      visibleColumns
    };

    const updatedViews = [...savedViews, view];
    setSavedViews(updatedViews);
    localStorage.setItem('rebateViews', JSON.stringify(updatedViews));
    setShowSaveViewModal(false);
    setViewName("");
  };

  // Load saved view
  const loadSavedView = (view) => {
    setTeamLeaderFilter(view.filters.teamLeader || "");
    setStatusFilter(view.filters.status || "");
    setDateFrom(view.filters.dateFrom || "");
    setDateTo(view.filters.dateTo || "");
    setLobFilter(view.filters.lob || "");
    setSearchText(view.filters.searchText || "");
    setSortConfig(view.sort || { key: null, direction: 'ascending' });
    setVisibleColumns(view.visibleColumns || {});

    setTimeout(() => handleFilter(), 0);
  };

  // Delete saved view
  const deleteSavedView = (id) => {
    const updatedViews = savedViews.filter(view => view.id !== id);
    setSavedViews(updatedViews);
    localStorage.setItem('rebateViews', JSON.stringify(updatedViews));
  };

  // Toggle row expansion
  const toggleRowExpansion = (index) => {
    setExpandedRows(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // ======= CHART DATA PREPARATION =======

  // Pie chart data
  const pieChartData = [
    { name: "Approved", value: approvedCount, color: "#4CAF50" },
    { name: "Pending", value: pendingCount, color: "#FFF000" },
    { name: "Rejected", value: rejectedCount, color: "#F44336" }
  ];

  // Bar chart data - Pending by Team Leader
  const pendingByTL = {};
  filteredRebates.forEach(doc => {
    const status = (doc["TL Status"] || "").toLowerCase().trim();
    const isPending =
      doc["TL Status"] === null ||
      status === "for approval" ||
      status === "pending" ||
      status === "";
    if (isPending) {
      const tl = doc["Team Leader"] || "Unknown";
      pendingByTL[tl] = (pendingByTL[tl] || 0) + 1;
    }
  });
  

  const barChartData = Object.keys(pendingByTL)
    .map(tlName => ({
      name: tlName,
      pendingCount: pendingByTL[tlName]
    }))
    .sort((a, b) => b.pendingCount - a.pendingCount);

  const barChartHeight = Math.max(barChartData.length * 40, 300);

  // Trend chart data
  const trendData = getTrendData();

  // Pagination logic
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = sortedData.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(sortedData.length / recordsPerPage);
  const COLORS = ["#FFF000", "#2D2D2D"];
  // Animation helper classes
  const getAnimationClass = (stage) => {
    return animationStage >= stage
      ? 'opacity-100 translate-y-0'
      : 'opacity-0 translate-y-4';
  };

  // ======= RENDER METHODS =======

  // Render chart based on active type
  const renderActiveChart = () => {
    if (activeChartType === "pie") {
      // Add this function to calculate aging days right after the formatDate function
      const calculateAgingDays = (dateString) => {
        if (!dateString) return 0;

        const dateCreated = new Date(dateString);
        if (isNaN(dateCreated.getTime())) return 0;

        const today = new Date();
        const diffTime = Math.abs(today - dateCreated);
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        return diffDays;
      };

      // Add this function to classify the aging into buckets for better visualization
      const getAgingCategory = (days) => {
        if (days <= 7) return "1-7 days";
        if (days <= 14) return "8-14 days";
        if (days <= 30) return "15-30 days";
        if (days <= 60) return "31-60 days";
        return "60+ days";
      };

      // Replace the pieChartData definition with this improved version that shows aging
      const getAgingChartData = () => {
        // Filter only pending or "for approval" rebates
        const pendingRebates = filteredRebates.filter(doc => {
          const status = (doc["TL Status"] || "").toLowerCase().trim();
          return doc["TL Status"] === null || status === "for approval" || status === "pending" || status === "";
        });

        // Calculate aging days for each pending rebate
        const agingData = pendingRebates.map(rebate => {
          const agingDays = calculateAgingDays(rebate["Date Today"]);
          return {
            ...rebate,
            agingDays,
            agingCategory: getAgingCategory(agingDays)
          };
        });

        // Group by aging category
        const agingGroups = {};
        agingData.forEach(rebate => {
          const category = rebate.agingCategory;
          if (!agingGroups[category]) {
            agingGroups[category] = {
              count: 0,
              totalAmount: 0,
              rebates: []
            };
          }

          agingGroups[category].count++;

          // Add amount if available
          let amtStr = rebate["Rebate Amount"] || "0";
          if (typeof amtStr === "string") {
            amtStr = amtStr.replace(/\n/g, "").trim();
          }
          const amt = parseFloat(amtStr);
          if (!isNaN(amt)) {
            agingGroups[category].totalAmount += amt;
          }

          agingGroups[category].rebates.push(rebate);
        });

        // Convert to array for chart display
        // Order categories from newest to oldest
        const orderedCategories = ["1-7 days", "8-14 days", "15-30 days", "31-60 days", "60+ days"];

        const chartData = orderedCategories
          .filter(category => agingGroups[category]) // Only include categories that exist
          .map(category => ({
            name: category,
            value: agingGroups[category].count,
            amount: agingGroups[category].totalAmount.toFixed(2),
            color: getAgingColor(category),
            rebates: agingGroups[category].rebates
          }));

        return chartData;
      };

      // Add a function to assign colors based on aging severity
      const getAgingColor = (category) => {
        switch (category) {
          case "1-7 days":
            return "#4CAF50"; // Green - recent
          case "8-14 days":
            return "#8BC34A"; // Light green
          case "15-30 days":
            return "#FFC107"; // Amber - moderate concern
          case "31-60 days":
            return "#FF9800"; // Orange - high concern
          case "60+ days":
            return "#F44336"; // Red - critical
          default:
            return "#9E9E9E"; // Gray
        }
      };

      // Then modify the renderActiveChart function to use this new data for the pie chart
      // Replace the current pie chart code in renderActiveChart() with this:
      if (activeChartType === "pie") {
        // Check if we're on the 'pending' tab to show aging chart
        if (activeTab === 'pending') {
          // Get the aging data
          const agingChartData = getAgingChartData();

          // If no data, show a placeholder
          if (agingChartData.length === 0) {
            return (
              <div className="flex items-center justify-center h-64 flex-col">
                <div className="text-gray-400 text-lg mb-2">No pending rebates found</div>
                <div className="text-gray-400 text-sm">Apply filters to view aging data</div>
              </div>
            );
          }

          // Calculate the total number of pending rebates for percentage calculation
          const totalPending = agingChartData.reduce((sum, item) => sum + item.value, 0);

          return (
            <div>
              <h3 className="text-center text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                Aging of Pending Rebates
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={agingChartData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    innerRadius={60}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, value, percent }) =>
                      `${name}: ${value} (${(percent * 100).toFixed(0)}%)`
                    }
                    animationDuration={1500}
                  >
                    {agingChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name, props) => {
                      // Display both count and amount
                      return [
                        `Count: ${value}`,
                        `Amount: ₱${props.payload.amount}`,
                        `Aging: ${name}`
                      ];
                    }}
                    contentStyle={{
                      backgroundColor: 'var(--tooltip-bg, #F4F2F2)',
                      border: 'none',
                      borderRadius: '4px',
                      color: 'var(--tooltip-text, #1F2937)'
                    }}
                  />
                  <Legend
                    iconSize={10}
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                    formatter={(value, entry) => {
                      // Show percentage in legend
                      const percent = (entry.payload.value / totalPending * 100).toFixed(0);
                      return `${value} (${percent}%)`;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="text-center text-xs text-gray-500 dark:text-gray-400 mt-2">
                Shows the distribution of pending rebates by age (days since creation)
              </div>
            </div>
          );
        } else {
          // For other tabs (not pending), show the regular pie chart
          return (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  innerRadius={60}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  animationDuration={1500}
                >
                  {pieChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name) => [`${value} rebates`, name]}
                  contentStyle={{
                    backgroundColor: 'var(--tooltip-bg, #F4F2F2)',
                    border: 'none',
                    borderRadius: '4px',
                    color: 'var(--tooltip-text, #1F2937)'
                  }}
                />
                <Legend iconSize={10} layout="horizontal" verticalAlign="bottom" align="center" />
              </PieChart>
            </ResponsiveContainer>
          );
        }
      }
    } else if (activeChartType === "bar") {
      // Check if we're on the 'pending' tab to show aging distribution
      if (activeTab === 'pending') {
        // Get aging data with all pending rebates
        const agingData = getAgingDualViewData();

        // Group aging data by Team Leader - use allData instead of tableData
        const teamLeaderGroups = {};

        // Process all pending rebates, not just the top 10
        agingData.tableData.forEach(rebate => {
          const teamLeader = rebate["Team Leader"] || "Unknown";

          if (!teamLeaderGroups[teamLeader]) {
            teamLeaderGroups[teamLeader] = {
              name: teamLeader,
              totalCount: 0,
              totalAmount: 0,
              agingBreakdown: {
                "1-7 days": 0,
                "8-14 days": 0,
                "15-30 days": 0,
                "31-60 days": 0,
                "60+ days": 0
              }
            };
          }

          teamLeaderGroups[teamLeader].totalCount++;
          teamLeaderGroups[teamLeader].totalAmount += rebate.rebateAmount;
          teamLeaderGroups[teamLeader].agingBreakdown[rebate.category]++;
        });

        // Convert to array and sort by total count (without limiting to 10)
        const teamLeaderData = Object.values(teamLeaderGroups)
          .sort((a, b) => b.totalCount - a.totalCount);

        // Compute total pages for the TL chart pagination
        const totalTlChartPages = Math.ceil(teamLeaderData.length / tlChartItemsPerPage);

        // Get paginated data for the current page
        const paginatedTeamLeaderData = teamLeaderData.slice(
          (tlChartPage - 1) * tlChartItemsPerPage,
          tlChartPage * tlChartItemsPerPage
        );

        return (
          <div className="flex flex-col space-y-4">
            {/* Chart Title and Controls */}
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-lg font-medium text-gray-700 dark:text-gray-300">
                Pending Rebates by Team Leader and Age
              </h4>
              <div className="flex items-center space-x-2">
                <label className="text-sm text-gray-600 dark:text-gray-400">Items per page:</label>
                <select
                  className="border border-gray-300 dark:border-gray-600 rounded-lg p-1 text-sm bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                  value={tlChartItemsPerPage}
                  onChange={(e) => {
                    setTlChartPage(1);
                    setTlChartItemsPerPage(Number(e.target.value));
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={20}>20</option>
                </select>
              </div>
            </div>

            {/* Bar Chart with Dynamic Height */}
            <ResponsiveContainer
              width="100%"
              height={Math.min(Math.max(paginatedTeamLeaderData.length * 50, 300), 600)}
            >
              <BarChart
                data={paginatedTeamLeaderData}
                layout="vertical"
                margin={{ top: 10, right: 30, left: 110, bottom: 20 }}
                barGap={0}
                barCategoryGap={15}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(160, 160, 160, 0.3)" />
                <XAxis
                  type="number"
                  allowDecimals={false}
                  tickFormatter={(value) => value.toLocaleString()}
                  domain={[0, 'dataMax']}
                  label={{
                    value: 'Number of Rebates',
                    position: 'insideBottom',
                    offset: -10,
                    fill: 'var(--text-color, #555)',
                    fontSize: 12
                  }}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 12 }}
                  width={110}
                  tickFormatter={(value) => value.length > 14 ? `${value.substring(0, 14)}...` : value}
                />
                <Tooltip
                  formatter={(value, name, props) => {
                    // Map category names to colors
                    const colorMap = {
                      "1-7 days": "#4CAF50",
                      "8-14 days": "#8BC34A",
                      "15-30 days": "#FFC107",
                      "31-60 days": "#FF9800",
                      "60+ days": "#F44336"
                    };

                    return [
                      <span style={{ color: colorMap[name] || '#fff' }}>
                        {value.toLocaleString()} rebates
                      </span>,
                      <span style={{ fontWeight: 'bold' }}>{name}</span>
                    ];
                  }}
                  contentStyle={{
                    backgroundColor: '#2D2D2D',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '10px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
                    color: 'white'
                  }}
                  itemStyle={{ padding: '4px 0' }}
                  labelStyle={{ fontWeight: 'bold', marginBottom: '5px' }}
                  cursor={{ fill: 'rgba(180, 180, 180, 0.1)' }}
                />
                <Legend
                  layout="horizontal"
                  verticalAlign="top"
                  align="center"
                  wrapperStyle={{ paddingBottom: '10px' }}
                  iconSize={12}
                  iconType="circle"
                />

                {/* Display stacked bars for each aging category */}
                <Bar
                  dataKey="agingBreakdown.1-7 days"
                  name="1-7 days"
                  stackId="a"
                  fill="#4CAF50"
                  barSize={24}
                  radius={[0, 0, 0, 0]}
                  animationDuration={1500}
                  animationEasing="ease-out"
                />
                <Bar
                  dataKey="agingBreakdown.8-14 days"
                  name="8-14 days"
                  stackId="a"
                  fill="#8BC34A"
                  animationDuration={1500}
                  animationEasing="ease-out"
                />
                <Bar
                  dataKey="agingBreakdown.15-30 days"
                  name="15-30 days"
                  stackId="a"
                  fill="#FFC107"
                  animationDuration={1500}
                  animationEasing="ease-out"
                />
                <Bar
                  dataKey="agingBreakdown.31-60 days"
                  name="31-60 days"
                  stackId="a"
                  fill="#FF9800"
                  animationDuration={1500}
                  animationEasing="ease-out"
                />
                <Bar
                  dataKey="agingBreakdown.60+ days"
                  name="60+ days"
                  stackId="a"
                  fill="#F44336"
                  radius={[0, 4, 4, 0]}
                  animationDuration={1500}
                  animationEasing="ease-out"
                />
              </BarChart>
            </ResponsiveContainer>

            {/* Pagination Controls */}
            {teamLeaderData.length > tlChartItemsPerPage && (
              <div className="flex justify-between items-center pt-3 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Showing {tlChartPage * tlChartItemsPerPage - tlChartItemsPerPage + 1} to {Math.min(tlChartPage * tlChartItemsPerPage, teamLeaderData.length)} of {teamLeaderData.length} team leaders
                </div>

                <div className="flex space-x-1">
                  <button
                    className="p-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    onClick={() => setTlChartPage(1)}
                    disabled={tlChartPage === 1}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7"></path>
                    </svg>
                  </button>

                  <button
                    className="p-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    onClick={() => setTlChartPage(prev => Math.max(prev - 1, 1))}
                    disabled={tlChartPage === 1}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                    </svg>
                  </button>

                  <div className="flex items-center px-2">
                    {Array.from({ length: Math.min(5, totalTlChartPages) }).map((_, i) => {
                      // Logic to display appropriate page numbers
                      let pageNum;
                      if (totalTlChartPages <= 5) {
                        pageNum = i + 1;
                      } else if (tlChartPage <= 3) {
                        pageNum = i === 4 ? totalTlChartPages : i + 1;
                      } else if (tlChartPage >= totalTlChartPages - 2) {
                        pageNum = i === 0 ? 1 : totalTlChartPages - (4 - i);
                      } else {
                        if (i === 0) pageNum = 1;
                        else if (i === 4) pageNum = totalTlChartPages;
                        else pageNum = tlChartPage + (i - 2);
                      }

                      // Show ellipsis where needed
                      if ((totalTlChartPages > 5) &&
                        ((i === 1 && pageNum !== 2) || (i === 3 && pageNum !== totalTlChartPages - 1))) {
                        return <span key={i} className="px-1 text-gray-500 dark:text-gray-400">...</span>;
                      }

                      return (
                        <button
                          key={i}
                          className={`w-8 h-8 mx-1 rounded-full focus:outline-none ${tlChartPage === pageNum
                            ? 'bg-uasYellow text-laryzeDarkGray font-medium'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                            }`}
                          onClick={() => setTlChartPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    className="p-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    onClick={() => setTlChartPage(prev => Math.min(prev + 1, totalTlChartPages))}
                    disabled={tlChartPage === totalTlChartPages}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                    </svg>
                  </button>

                  <button
                    className="p-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    onClick={() => setTlChartPage(totalTlChartPages)}
                    disabled={tlChartPage === totalTlChartPages}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5l7 7-7 7M5 5l7 7-7 7"></path>
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* No Data Message */}
            {teamLeaderData.length === 0 && (
              <div className="flex flex-col items-center justify-center py-10 text-gray-400 dark:text-gray-500">
                <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                <p className="text-center">No pending rebate data available.</p>
              </div>
            )}
          </div>
        );
      } else {
        // Original bar chart for non-pending tabs
        return (
          <ResponsiveContainer width="100%" height={barChartHeight}>
            <BarChart
              data={barChartData.slice(0, 10)} // Take top 10 for better visibility
              layout="vertical"
              margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#444" />
              <XAxis type="number" allowDecimals={false} />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: 12 }}
                width={150}
              />
              <Tooltip
                formatter={(value) => [`${value} pending`, 'Count']}
                contentStyle={{
                  backgroundColor: '#2D2D2D',
                  border: 'none',
                  borderRadius: '4px',
                  color: 'white'
                }}
              />
              <Bar
                dataKey="pendingCount"
                fill="#FFF000"
                name="Pending Count"
                barSize={20}
                radius={[0, 4, 4, 0]}
                animationDuration={1500}
              />
            </BarChart>
          </ResponsiveContainer>
        );
      }
    }// In the renderActiveChart function, replace the existing trend chart code:
    // For the trend chart case, with only the aging table (no pie chart)
    else if (activeChartType === "trend") {
      // Check if we're on the 'pending' tab to show aging view
      if (activeTab === 'pending') {
        const { tableData, totalPending, totalAmount } = getAgingDualViewData();

        // If no data, show a placeholder
        if (tableData.length === 0) {
          return (
            <div className="flex items-center justify-center h-64 flex-col">
              <div className="text-gray-400 text-lg mb-2">No pending rebates found</div>
              <div className="text-gray-400 text-sm">Apply filters to view aging data</div>
            </div>
          );
        }

        // Compute total pages for the trend table pagination
        const totalTrendPages = Math.ceil(tableData.length / trendChartItemsPerPage);

        // Get paginated data for the current page
        const paginatedTrendData = tableData.slice(
          (trendChartPage - 1) * trendChartItemsPerPage,
          trendChartPage * trendChartItemsPerPage
        );

        return (
          <div>
            <div className="mb-4 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300">
                Pending Rebates by Aging ({totalPending} total)
              </h3>
              <div className="flex items-center space-x-2">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Total Amount: ₱{totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <select
                  className="ml-4 border border-gray-300 dark:border-gray-600 rounded-lg p-1 text-sm bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                  value={trendChartItemsPerPage}
                  onChange={(e) => {
                    setTrendChartPage(1);
                    setTrendChartItemsPerPage(Number(e.target.value));
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={20}>20</option>
                </select>
              </div>
            </div>

            {/* Oldest Pending Tickets Table (full width) */}
            <div className="bg-white dark:bg-laryzeDarkGray rounded-lg shadow-md p-4 mb-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Oldest Pending Rebates
              </h4>
              <div className="overflow-auto max-h-96">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ticket #</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Agent</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Team Leader</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Days</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Amount</th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date Created</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {paginatedTrendData.map((item, index) => (
                      <tr key={index} className={`hover:bg-gray-50 dark:hover:bg-gray-800 ${item.agingDays > 30 ? 'bg-red-50 dark:bg-red-900 bg-opacity-30 dark:bg-opacity-20' : ''
                        }`}>
                        <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-800 dark:text-gray-200">{item.ticketNumber}</td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-800 dark:text-gray-200">{item.agentName}</td>
                        <td className="px-3 py-2 text-xs whitespace-nowrap text-gray-800 dark:text-gray-200">{item["Team Leader"] || "-"}</td>
                        <td className="px-3 py-2 text-xs text-right whitespace-nowrap">
                          <span className="font-medium rounded-full px-2 py-1"
                            style={{
                              backgroundColor: `${getAgingColor(item.category)}20`, // 20% opacity
                              color: getAgingColor(item.category)
                            }}>
                            {item.agingDays}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs text-right whitespace-nowrap text-gray-800 dark:text-gray-200">
                          ₱{item.rebateAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-3 py-2 text-xs text-center whitespace-nowrap text-gray-800 dark:text-gray-200">
                          {new Date(item["Date Today"]).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination Controls */}
            {tableData.length > trendChartItemsPerPage && (
              <div className="flex justify-between items-center pt-3 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Showing {trendChartPage * trendChartItemsPerPage - trendChartItemsPerPage + 1} to {Math.min(trendChartPage * trendChartItemsPerPage, tableData.length)} of {tableData.length} pending rebates
                </div>

                <div className="flex space-x-1">
                  <button
                    className="p-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    onClick={() => setTrendChartPage(1)}
                    disabled={trendChartPage === 1}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7"></path>
                    </svg>
                  </button>

                  <button
                    className="p-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    onClick={() => setTrendChartPage(prev => Math.max(prev - 1, 1))}
                    disabled={trendChartPage === 1}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                    </svg>
                  </button>

                  <div className="flex items-center px-2">
                    {Array.from({ length: Math.min(5, totalTrendPages) }).map((_, i) => {
                      // Logic to display appropriate page numbers
                      let pageNum;
                      if (totalTrendPages <= 5) {
                        pageNum = i + 1;
                      } else if (trendChartPage <= 3) {
                        pageNum = i === 4 ? totalTrendPages : i + 1;
                      } else if (trendChartPage >= totalTrendPages - 2) {
                        pageNum = i === 0 ? 1 : totalTrendPages - (4 - i);
                      } else {
                        if (i === 0) pageNum = 1;
                        else if (i === 4) pageNum = totalTrendPages;
                        else pageNum = trendChartPage + (i - 2);
                      }

                      // Show ellipsis where needed
                      if ((totalTrendPages > 5) &&
                        ((i === 1 && pageNum !== 2) || (i === 3 && pageNum !== totalTrendPages - 1))) {
                        return <span key={i} className="px-1 text-gray-500 dark:text-gray-400">...</span>;
                      }

                      return (
                        <button
                          key={i}
                          className={`w-8 h-8 mx-1 rounded-full focus:outline-none ${trendChartPage === pageNum
                            ? 'bg-uasYellow text-laryzeDarkGray font-medium'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                            }`}
                          onClick={() => setTrendChartPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    className="p-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    onClick={() => setTrendChartPage(prev => Math.min(prev + 1, totalTrendPages))}
                    disabled={trendChartPage === totalTrendPages}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                    </svg>
                  </button>

                  <button
                    className="p-2 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 disabled:opacity-50 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    onClick={() => setTrendChartPage(totalTrendPages)}
                    disabled={trendChartPage === totalTrendPages}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5l7 7-7 7M5 5l7 7-7 7"></path>
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      } else {
        // For other tabs (not pending), show the regular trend chart
        return (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart
              data={trendData}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#FFF000" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#FFF000" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="date" />
              <YAxis />
              <CartesianGrid strokeDasharray="3 3" />
              <Tooltip
                formatter={(value, name) => [`${value} rebates`, 'Total Count']}
                contentStyle={{
                  backgroundColor: '#2D2D2D',
                  border: 'none',
                  borderRadius: '4px',
                  color: 'white'
                }}
              />
              <Area
                type="monotone"
                dataKey="total"
                stroke="#FFF000"
                fillOpacity={1}
                fill="url(#colorTotal)"
                name="Total Rebates"
                animationDuration={1500}
              />
            </AreaChart>
          </ResponsiveContainer>
        );
      }
    }
  };

  // Help tooltip component
  const HelpTooltip = ({ id, text }) => (
    <div className="relative inline-block">
      <div
        className="ml-1 w-4 h-4 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 inline-flex items-center justify-center text-xs cursor-help"
        onMouseEnter={() => setShowHelpTooltip({ ...showHelpTooltip, [id]: true })}
        onMouseLeave={() => setShowHelpTooltip({ ...showHelpTooltip, [id]: false })}
      >
        ?
      </div>
      {showHelpTooltip[id] && (
        <div className="absolute z-[9999] w-64 bg-laryzeDarkGray text-white text-xs rounded-md shadow-lg p-3 -mt-2 ml-6">
          {text}
          <div className="absolute left-0 top-3 transform -translate-x-2 rotate-45 w-3 h-3 bg-laryzeDarkGray"></div>
        </div>
      )}
    </div>
  );

  // Keyboard shortcuts helper
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Alt + F: Focus search
      if (e.altKey && e.key === 'f') {
        e.preventDefault();
        document.getElementById('search-input')?.focus();
      }

      // Alt + C: Clear filters
      if (e.altKey && e.key === 'c') {
        e.preventDefault();
        clearAllFilters();
      }

      // Alt + Right/Left: Pagination
      if (e.altKey && e.key === 'ArrowRight' && currentPage < totalPages) {
        e.preventDefault();
        setCurrentPage(prev => prev + 1);
      }
      if (e.altKey && e.key === 'ArrowLeft' && currentPage > 1) {
        e.preventDefault();
        setCurrentPage(prev => prev - 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentPage, totalPages]);
  // Add this function to calculate aging days right after the formatDate function
  const calculateAgingDays = (dateString) => {
    if (!dateString) return 0;

    const dateCreated = new Date(dateString);
    if (isNaN(dateCreated.getTime())) return 0;

    const today = new Date();
    const diffTime = Math.abs(today - dateCreated);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  };

  // Add this function to classify the aging into buckets for better visualization
  const getAgingCategory = (days) => {
    if (days <= 7) return "1-7 days";
    if (days <= 14) return "8-14 days";
    if (days <= 30) return "15-30 days";
    if (days <= 60) return "31-60 days";
    return "60+ days";
  };

  // Add a function to assign colors based on aging severity
  const getAgingColor = (category) => {
    switch (category) {
      case "1-7 days":
        return "#4CAF50"; // Green - recent
      case "8-14 days":
        return "#8BC34A"; // Light green
      case "15-30 days":
        return "#FFC107"; // Amber - moderate concern
      case "31-60 days":
        return "#FF9800"; // Orange - high concern
      case "60+ days":
        return "#F44336"; // Red - critical
      default:
        return "#9E9E9E"; // Gray
    }
  };

  const downloadCSV = () => {
    if (!filteredRebates || filteredRebates.length === 0) {
      alert("No data available to download.");
      return;
    }

    // Get visible columns only
    const visibleKeys = Object.keys(visibleColumns).filter(key => visibleColumns[key]);
    if (visibleKeys.length === 0) {
      alert("No columns selected for download.");
      return;
    }

    // Create CSV header
    const header = visibleKeys.join(",");

    // Create CSV rows
    const rows = filteredRebates.map(row => {
      return visibleKeys.map(key => {
        const rawValue = row[key] ?? "";
        // Clean up and handle commas/newlines
        const cleaned = typeof rawValue === "string"
          ? `"${rawValue.replace(/"/g, '""').replace(/\n/g, ' ')}"`
          : rawValue;
        return cleaned;
      }).join(",");
    });

    // Combine header + rows
    const csvContent = [header, ...rows].join("\n");

    // Trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `rebate_data_${new Date().toISOString().split("T")[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const hasCheckedRows = useMemo(() => {
    return Object.values(checkedRows).some(checked => checked);
  }, [checkedRows]);

  // Add this function to handle individual row checkbox clicks
  const handleRowCheck = (index) => {
    const newCheckedRows = { ...checkedRows };
    newCheckedRows[index] = !newCheckedRows[index];
    setCheckedRows(newCheckedRows);

    // Update allChecked state based on current page's eligible rows
    updateSelectAllCheckboxState(newCheckedRows);
  };
  // Add this function to handle "select all" checkbox
  const handleSelectAll = () => {
    const newCheckedRows = { ...checkedRows };
    const newAllChecked = !allChecked;

    // Update only rows that are pending or for approval on current page
    currentRecords.forEach((doc, i) => {
      const rowIndex = indexOfFirstRecord + i;
      const isEligible = doc["TL Status"] === null ||
        (doc["TL Status"] || "").toLowerCase().trim() === "for approval" ||
        (doc["TL Status"] || "").toLowerCase().trim() === "pending" ||
        (doc["TL Status"] || "").toLowerCase().trim() === "";

      // Only set checked state for eligible rows
      if (isEligible) {
        newCheckedRows[rowIndex] = newAllChecked;
      }
    });

    setCheckedRows(newCheckedRows);
    setAllChecked(newAllChecked);
  };


  // Add this function to handle approving selected rebates
  const handleApproveSelected = async () => {
    const selectedRebates = Object.keys(checkedRows)
      .filter(index => checkedRows[index])
      .map(index => filteredRebates[index]);

    if (selectedRebates.length === 0) {
      toast.error("No rebates selected for approval");
      return;
    }

    const ticketsToUpdate = selectedRebates.map(rebate => rebate["Ticket Number"]);
    const approver = JSON.parse(localStorage.getItem("user"))?.name;

    try {
      const res = await axios.post("http://localhost:5000/api/rebate/approve-rebates", {
        tickets: ticketsToUpdate,
        approverName: approver
      });

      if (res.data.success) {
        toast.success(`Approved ${res.data.modifiedCount} rebate(s).`);

        // Refetch updated data
        const updatedRes = await axios.get("http://localhost:5000/api/rebate/supervisor-rebate-data");
        setRebates(updatedRes.data.data || []);
        setFilteredRebates(updatedRes.data.data || []);
        setCheckedRows({});
        setAllChecked(false);
      } else {
        toast.error("Approval failed: " + res.data.message);
      }
    } catch (err) {
      toast.error("Error approving: " + (err.response?.data?.message || err.message));
    }
  };


  const handleCancelSelected = async () => {
    const selectedRebates = Object.keys(checkedRows)
      .filter(index => checkedRows[index])
      .map(index => filteredRebates[index]);

    if (selectedRebates.length === 0) {
      toast.error("No rebates selected to cancel");
      return;
    }

    const ticketsToUpdate = selectedRebates.map(rebate => rebate["Ticket Number"]);
    const approver = JSON.parse(localStorage.getItem("user"))?.name;

    try {
      const res = await axios.post("http://localhost:5000/api/rebate/cancel-rebates", {
        tickets: ticketsToUpdate,
        approverName: approver
      });

      if (res.data.success) {
        toast.success(`Cancelled ${res.data.modifiedCount} rebate(s).`);

        // ✅ Refetch updated rebate data
        const updatedRes = await axios.get("http://localhost:5000/api/rebate/supervisor-rebate-data");
        setRebates(updatedRes.data.data || []);
        setFilteredRebates(updatedRes.data.data || []);
        setCheckedRows({});
        setAllChecked(false);
      } else {
        toast.error("Cancellation failed: " + res.data.message);
      }
    } catch (err) {
      toast.error("Error cancelling: " + (err.response?.data?.message || err.message));
    }
  };



  const handleMarkForApprovalSelected = async () => {
    const selectedRebates = Object.keys(checkedRows)
      .filter(index => checkedRows[index])
      .map(index => filteredRebates[index]);

    if (selectedRebates.length === 0) {
      toast.error("No rebates selected to mark for approval");
      return;
    }

    const ticketsToUpdate = selectedRebates.map(rebate => rebate["Ticket Number"]);
    const userName = JSON.parse(localStorage.getItem("user"))?.name;

    try {
      const res = await axios.post("http://localhost:5000/api/rebate/mark-for-approval", {
        tickets: ticketsToUpdate,
        userName: userName
      });

      if (res.data.success) {
        toast.success(`Marked ${res.data.modifiedCount} rebate(s) as "For Approval".`);

        // ✅ Refetch updated rebate data
        const updatedRes = await axios.get("http://localhost:5000/api/rebate/supervisor-rebate-data");
        setRebates(updatedRes.data.data || []);
        setFilteredRebates(updatedRes.data.data || []);
        setCheckedRows({});
        setAllChecked(false);
      } else {
        toast.error("Marking failed: " + res.data.message);
      }
    } catch (err) {
      toast.error("Error marking for approval: " + (err.response?.data?.message || err.message));
    }
  };

  const handleDeleteSelected = async () => {
    const confirmed = window.confirm("Are you sure you want to permanently delete the selected tickets?");
    if (!confirmed) return;
  
    const selectedTickets = Object.entries(checkedRows)
      .filter(([_, isChecked]) => isChecked)
      .map(([index]) => sortedData[parseInt(index)]?.["Ticket Number"]);
  
    if (selectedTickets.length === 0) {
      toast.error("No tickets selected for deletion.");
      return;
    }
  
    const userName = JSON.parse(localStorage.getItem("user"))?.name;
  
    try {
      const response = await axios.post("http://localhost:5000/api/rebate/delete-rebates", {
        tickets: selectedTickets,
        userName: userName
      });
  
      if (response.data.success) {
        toast.success(`${response.data.deletedCount} ticket(s) deleted successfully.`);
        
        // ✅ Refetch or re-filter data
        const updated = rebates.filter(rebate => !selectedTickets.includes(rebate["Ticket Number"]));
        setRebates(updated);
        setFilteredRebates(updated);
        setCheckedRows({});
        updateSelectAllCheckboxState({});
      } else {
        toast.error("Deletion failed: " + response.data.message);
      }
    } catch (error) {
      console.error("❌ Delete error:", error);
      toast.error("An error occurred during deletion.");
    }
  };
  



  useEffect(() => {
    // Update the "Select All" checkbox state whenever the current page changes
    updateSelectAllCheckboxState();
  }, [currentPage, recordsPerPage, filteredRebates]);
  // Render the component
  return (
    <div className="relative">
      <LoadingScreen isVisible={loading} />
      <div className={`transition-opacity duration-700 delay-300 
        ${!loading && isLoaded ? 'opacity-100' : 'opacity-0'}`}>
        <div className="p-4 md:p-6 lg:p-8 bg-gray-50 dark:bg-laryzeDarkGray bg-opacity-40 dark:bg-opacity-40 min-h-screen">
          {/* Header Section - Enhanced with better animations */}
          <div className={`bg-white dark:bg-laryzeDarkGray rounded-xl shadow-lg mb-8 transition-all duration-700 ease-in-out transform ${isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
            <div className="md:flex">
              <div className="p-8 md:p-10 flex-grow">
                <div className={`flex items-center mb-4 transition-all duration-700 delay-100 transform ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                  <div className="w-2 h-8 bg-uasYellow mr-3 rounded-md"></div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                    Rebate Management Dashboard
                  </h2>
                  <div className="ml-auto flex space-x-2">
                    {savedViews.length > 0 && (
                      <div className="relative">
                        <button
                          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-uasYellow rounded"
                          onClick={() => setShowHelpTooltip({ ...showHelpTooltip, savedViews: !showHelpTooltip.savedViews })}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"></path>
                          </svg>
                        </button>
                        {showHelpTooltip.savedViews && (
                          <div className="absolute right-0 z-50 w-64 mt-2 bg-white dark:bg-laryzeDarkGray rounded-lg shadow-lg overflow-hidden">
                            <div className="p-3 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                              <h3 className="font-medium text-gray-700 dark:text-gray-300">Saved Views</h3>
                            </div>
                            <div className="max-h-60 overflow-y-auto">
                              {savedViews.map(view => (
                                <div key={view.id} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex justify-between items-center">
                                  <button
                                    className="text-left text-sm text-gray-700 dark:text-gray-300 w-full px-2 py-1"
                                    onClick={() => loadSavedView(view)}
                                  >
                                    {view.name}
                                  </button>
                                  <button
                                    className="text-red-400 hover:text-red-600 p-1 rounded-full"
                                    onClick={() => deleteSavedView(view.id)}
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                                    </svg>
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {showHelpTooltip.dashboard && (
                    <div className="absolute right-8 top-24 z-50 w-72 bg-laryzeDarkGray text-white text-sm rounded-md shadow-lg p-4">
                      <h3 className="font-semibold mb-2">Keyboard Shortcuts</h3>
                      <ul className="space-y-1 text-xs">
                        <li className="flex justify-between"><span>Alt + F</span><span>Focus search</span></li>
                        <li className="flex justify-between"><span>Alt + C</span><span>Clear all filters</span></li>
                        <li className="flex justify-between"><span>Alt + ←/→</span><span>Navigate pages</span></li>
                      </ul>
                      <div className="mt-3 pt-3 border-t border-gray-600">
                        <p className="text-xs">Use the tabs and quick filters for faster data access. Click column headers to sort.</p>
                      </div>
                      <button
                        className="absolute top-2 right-2 text-gray-400 hover:text-white"
                        onClick={() => setShowHelpTooltip({ ...showHelpTooltip, dashboard: false })}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
                <p className={`text-gray-600 dark:text-gray-300 text-lg mb-6 transition-all duration-700 delay-200 transform ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                  Monitor and manage rebate records, filter data, and view analytics.
                </p>
              </div>
              <div className={`hidden md:block w-full md:w-1/3 bg-gradient-to-br from-uasYellow to-yellow-400 dark:from-uasYellow dark:to-yellow-600 transition-all duration-1000 transform ${isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
                <div className="h-full flex items-center justify-center p-10">
                  <div className="text-center">
                    <h3 className="text-laryzeDarkGray text-2xl font-bold mb-2">Total Rebates</h3>
                    <div className="text-laryzeDarkGray dark:text-gray-900 text-4xl font-bold">
                      {typeof totalRebates === 'number'
                        ? new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(totalRebates)
                        : 'Loading...'}
                    </div>
                    <p className="text-laryzeDarkGray dark:text-gray-900 mt-2 text-sm">
                      Total value of all rebates in the system
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className={`mb-6 transition-all duration-700 transform ${getAnimationClass(1)}`}>
            <div className="border-b border-gray-200 dark:border-gray-700">
              <nav className="flex space-x-4">
                <button
                  className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors duration-200 ease-in-out ${activeTab === 'all'
                    ? 'border-uasYellow text-gray-800 dark:text-white'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  onClick={() => setActiveTab('all')}
                >
                  All Rebates
                </button>
                <button
                  className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors duration-200 ease-in-out ${activeTab === 'pending'
                    ? 'border-uasYellow text-gray-800 dark:text-white'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  onClick={() => setActiveTab('pending')}
                >
                  Pending | For Approval ({pendingCount})
                </button>
                <button
                  className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors duration-200 ease-in-out ${activeTab === 'approved'
                    ? 'border-uasYellow text-gray-800 dark:text-white'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  onClick={() => setActiveTab('approved')}
                >
                  Approved ({approvedCount})
                </button>
                <button
                  className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors duration-200 ease-in-out ${activeTab === 'rejected'
                    ? 'border-uasYellow text-gray-800 dark:text-white'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  onClick={() => setActiveTab('rejected')}
                >
                  Rejected ({rejectedCount})
                </button>
              </nav>
            </div>
          </div>


          {/* Top Stats (full data) */}
          <h3 className={`text-xl font-semibold text-gray-800 dark:text-white mb-6 transition-all duration-700 transform flex items-center ${getAnimationClass(1)}`}>
            <div className="w-1 h-6 bg-uasYellow mr-2 rounded-sm"></div>
            Overview
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard
              label="Total Rebates"
              value={totalRebates}
              icon={<BarChart2 className="w-6 h-6" />}
              isLoaded={isLoaded}
              stage={animationStage}
              requiredStage={1}
            />
            <StatCard
              label="Total Rebate Amount"
              value={`₱ ${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              icon={<Wallet className="w-6 h-6" />}
              isLoaded={isLoaded}
              stage={animationStage}
              requiredStage={1}
            />
            <StatCard
              label="Pending Rebates"
              value={pendingCount}
              icon={<Clock className="w-6 h-6" />}
              isLoaded={isLoaded}
              stage={animationStage}
              requiredStage={1}
            />
            <StatCard
              label="Approved Rebates"
              value={approvedCount}
              icon={<CheckCircle className="w-6 h-6" />}
              isLoaded={isLoaded}
              stage={animationStage}
              requiredStage={1}
            />
          </div>
          {activeTab == "all" && (
            <div className={`mb-8 transition-all duration-700 transform ${getAnimationClass(2)}`}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-gray-800 dark:text-white flex items-center">
                  <div className="w-1 h-6 bg-uasYellow mr-2 rounded-sm"></div>
                  Advanced Filters
                  <HelpTooltip
                    id="filterHelp"
                    text="Combine multiple filters to precisely target the rebate records you need. Toggle visibility to save space."
                  />
                </h3>
                <button
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  {showFilters ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"></path>
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  )}
                </button>
              </div>

              {showFilters && (
                <div className={`bg-white dark:bg-laryzeDarkGray shadow-lg rounded-xl p-6 mb-6 transition-all duration-500 ease-in-out transform origin-top border-l-4 border-uasYellow ${showFilters ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-0 h-0 overflow-hidden'}`}>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Team Leader</label>
                      <select
                        className="border border-gray-300 dark:border-gray-600 rounded-lg w-full p-2.5 text-sm bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 focus:ring-uasYellow focus:border-uasYellow"
                        value={teamLeaderFilter}
                        onChange={(e) => setTeamLeaderFilter(e.target.value)}
                      >
                        <option value="">All</option>
                        {teamLeaders.map((tl, idx) => (
                          <option key={idx} value={tl}>{tl}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Line of Business</label>
                      <select
                        className="border border-gray-300 dark:border-gray-600 rounded-lg w-full p-2.5 text-sm bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 focus:ring-uasYellow focus:border-uasYellow"
                        value={lobFilter}
                        onChange={(e) => setLobFilter(e.target.value)}
                      >
                        <option value="">All</option>
                        {lobList.map((lob, idx) => (
                          <option key={idx} value={lob}>{lob}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">TL Status</label>
                      <select
                        className="border border-gray-300 dark:border-gray-600 rounded-lg w-full p-2.5 text-sm bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 focus:ring-uasYellow focus:border-uasYellow"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                      >
                        <option value="">All</option>
                        {statuses.map((status, idx) => (
  <option key={idx} value={status}>
    {status.charAt(0).toUpperCase() + status.slice(1)}
  </option>
))}

                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date From</label>
                      <input
                        type="date"
                        className="border border-gray-300 dark:border-gray-600 rounded-lg w-full p-2.5 text-sm bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 focus:ring-uasYellow focus:border-uasYellow"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date To</label>
                      <input
                        type="date"
                        className="border border-gray-300 dark:border-gray-600 rounded-lg w-full p-2.5 text-sm bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-300 focus:ring-uasYellow focus:border-uasYellow"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                      />
                    </div>

                    <div className="md:col-span-5 flex justify-end space-x-3">
                      <button
                        className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium text-sm rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 focus:ring-4 focus:ring-gray-200 dark:focus:ring-gray-600 transition-all duration-300 flex items-center"
                        onClick={clearAllFilters}
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                        Clear All Filters
                        <kbd className="ml-2 px-1.5 text-xs bg-gray-100 dark:bg-gray-600 rounded">Alt+C</kbd>
                      </button>

                      <button
                        className="px-6 py-2.5 bg-laryzeDarkGray text-white font-medium text-sm rounded-lg hover:bg-uasYellow hover:text-laryzeDarkGray focus:ring-4 focus:ring-yellow-300 transition-all duration-300 transform hover:scale-105 flex items-center"
                        onClick={handleFilter}
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"></path>
                        </svg>
                        Apply Filters
                      </button>

                      <button
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium text-sm rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 focus:ring-4 focus:ring-gray-200 dark:focus:ring-gray-600 transition-all duration-300 flex items-center"
                        onClick={() => setShowSaveViewModal(true)}
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"></path>
                        </svg>
                        Save View
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Save View Modal */}
              {showSaveViewModal && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                  <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center">
                    <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                      <div className="absolute inset-0 bg-gray-500 dark:bg-gray-900 opacity-75"></div>
                    </div>
                    <div className="inline-block align-bottom bg-white dark:bg-laryzeDarkGray rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                      <div className="px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">Save Current View</h3>
                        <div>
                          <label htmlFor="viewName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">View Name</label>
                          <input
                            type="text"
                            id="viewName"
                            className="border border-gray-300 dark:border-gray-600 rounded-lg w-full p-2.5 text-sm bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:ring-uasYellow focus:border-uasYellow"
                            placeholder="My Saved View"
                            value={viewName}
                            onChange={(e) => setViewName(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                        <button
                          type="button"
                          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-uasYellow text-base font-medium text-laryzeDarkGray hover:bg-yellow-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-uasYellow sm:ml-3 sm:w-auto sm:text-sm"
                          onClick={saveCurrentView}
                        >
                          Save View
                        </button>
                        <button
                          type="button"
                          className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-laryzeDarkGray text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                          onClick={() => setShowSaveViewModal(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Charts */}
          <h3 className={`text-xl font-semibold text-gray-800 dark:text-white mb-6 transition-all duration-700 transform flex items-center ${getAnimationClass(3)}`}>
            <div className="w-1 h-6 bg-uasYellow mr-2 rounded-sm"></div>
            Analytics

          </h3>
          <div className="grid grid-cols-1 gap-8 mb-8">
            <div className={`bg-white dark:bg-laryzeDarkGray shadow-lg rounded-xl p-6 transition-all duration-700 transform hover:-translate-y-2 hover:shadow-xl border-t-4 border-uasYellow ${getAnimationClass(3)}`}>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-uasYellow" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"></path>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"></path>
                  </svg>
                  Rebate Status Overview
                </h3>

                <div className="flex space-x-1">
                  <button
                    className={`p-1 rounded ${activeChartType === 'pie' ? 'bg-gray-200 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                    onClick={() => setActiveChartType('pie')}
                    title="Pie Chart"
                  >
                    <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"></path>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"></path>
                    </svg>
                  </button>
                  <button
                    className={`p-1 rounded ${activeChartType === 'bar' ? 'bg-gray-200 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                    onClick={() => setActiveChartType('bar')}
                    title="Bar Chart"
                  >
                    <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                    </svg>
                  </button>
                  <button
                    className={`p-1 rounded ${activeChartType === 'trend' ? 'bg-gray-200 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                    onClick={() => setActiveChartType('trend')}
                    title="Trend Chart"
                  >
                    <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"></path>
                    </svg>
                  </button>
                </div>
              </div>

              {renderActiveChart()}
            </div>
            {activeTab == "all" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 overflow-auto max-h-96">
                {barChartData.map((tl, index) => (
                  <div key={index} className="bg-gray-50 dark:bg-laryzeDarkGray rounded-lg p-4 flex flex-col">
                    <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-auto">{tl.name}</h4>
                    <div className="flex items-center">
                      <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-600 rounded-full">
                        <div
                          className="h-2 bg-uasYellow rounded-full"
                          style={{ width: `${(tl.pendingCount / Math.max(...barChartData.map(d => d.pendingCount))) * 100}%` }}
                        />
                      </div>
                      <span className="ml-3 text-sm font-semibold text-gray-600 dark:text-gray-400">
                        {tl.pendingCount}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Table Controls */}
          <div className={`flex flex-col md:flex-row justify-between items-start md:items-center mb-4 transition-all duration-700 transform ${getAnimationClass(4)}`}>
            <h3 className="text-xl font-semibold text-gray-800 dark:text-white flex items-center mb-4 md:mb-0">
              <div className="w-1 h-6 bg-uasYellow mr-2 rounded-sm"></div>
              Rebate Records
              <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                ({filteredRebates.length} records)
              </span>
            </h3>



            <div className="flex flex-wrap gap-2">
              {/* Records per page select */}
              <div className="flex items-center">
                <label htmlFor="recordsPerPage" className="mr-2 text-sm text-gray-600 dark:text-gray-400">Show:</label>
                <select
                  id="recordsPerPage"
                  className="border border-gray-300 dark:border-gray-600 rounded-lg p-1.5 text-sm bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:ring-uasYellow focus:border-uasYellow"
                  value={recordsPerPage}
                  onChange={(e) => {
                    setRecordsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={250}>250</option>
                </select>
              </div>

              <div className="flex flex-wrap gap-2">
                {/* Search Textbox for Ticket Number */}
                <div className="flex items-center">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                      </svg>
                    </div>
                    <input
                      type="text"
                      id="search-input"
                      className="border border-gray-300 dark:border-gray-600 rounded-lg pl-10 pr-3 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 focus:ring-uasYellow focus:border-uasYellow w-48"
                      placeholder="Search Ticket Number..."
                      value={searchText}
                      onChange={(e) => {
                        setSearchText(e.target.value);
                        // Apply filter immediately when text changes
                        setTimeout(() => {
                          let filtered = [...rebates];

                          // Apply all existing filters first
                          if (teamLeaderFilter) {
                            filtered = filtered.filter(doc =>
                              (doc["Team Leader"] || "").toLowerCase().trim() === teamLeaderFilter.toLowerCase()
                            );
                          }

                          if (statusFilter) {
                            filtered = filtered.filter(doc =>
                              (doc["TL Status"] || "").toLowerCase().trim() === statusFilter.toLowerCase()
                            );
                          }

                          if (dateFrom) {
                            filtered = filtered.filter(doc => {
                              const dateValue = new Date(doc["Date Today"]);
                              return dateValue >= new Date(dateFrom);
                            });
                          }

                          if (dateTo) {
                            filtered = filtered.filter(doc => {
                              const dateValue = new Date(doc["Date Today"]);
                              return dateValue <= new Date(dateTo);
                            });
                          }

                          if (lobFilter) {
                            filtered = filtered.filter(doc =>
                              (doc["Line of Business"] || "")
                                .replace(/\n/g, "")
                                .trim()
                                .toLowerCase() === lobFilter.toLowerCase()
                            );
                          }

                          // Apply ticket number filter if there's search text
                          if (e.target.value) {
                            const searchLower = e.target.value.toLowerCase();
                            filtered = filtered.filter(doc => {
                              // Look specifically for Ticket Number
                              const ticketNumber = String(doc["Ticket Number"] || "").toLowerCase();
                              return ticketNumber.includes(searchLower);
                            });
                          }

                          setFilteredRebates(filtered);
                          setCurrentPage(1);
                        }, 300); // Small delay for performance
                      }}
                    />
                    {searchText && (
                      <button
                        className="absolute inset-y-0 right-0 flex items-center pr-2"
                        onClick={() => {
                          setSearchText('');
                          // Reset to show all results (with other filters applied)
                          setTimeout(() => handleFilter(), 0);
                        }}
                        title="Clear search"
                      >
                        <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                {/* Column visibility toggle */}
                <div className="relative">
                  {/* ... */}
                </div>
              </div>

              {/* Column visibility toggle */}
              <div className="relative">
                <button
                  className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center"
                  onClick={() => setShowColumnSelector(!showColumnSelector)}
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h8m-8 6h16"></path>
                  </svg>
                  Columns
                </button>

                {showColumnSelector && (
                  <div className="absolute bottom-full mb-2 right-0 w-72 bg-white dark:bg-laryzeDarkGray rounded-lg shadow-lg z-50 border border-gray-200 dark:border-gray-700">
                    <div className="p-3 border-b border-gray-200 dark:border-gray-700">
                      <h4 className="font-medium text-gray-700 dark:text-gray-300">Toggle Column Visibility</h4>
                    </div>
                    <div className="p-3 max-h-60 overflow-y-auto">
                      {rebates.length > 0 &&
                        Object.keys(rebates[0])
                          .filter(key => key !== "_id")
                          .map(key => (
                            <div key={key} className="flex items-center mb-2">
                              <input
                                type="checkbox"
                                id={`column-${key}`}
                                checked={visibleColumns[key] || false}
                                onChange={() => {
                                  setVisibleColumns({
                                    ...visibleColumns,
                                    [key]: !visibleColumns[key]
                                  });
                                }}
                                className="w-4 h-4 text-uasYellow focus:ring-uasYellow rounded checked:accent-black dark:checked:accent-uasYellow dark:checked:text-black"
                              />
                              <label htmlFor={`column-${key}`} className="ml-2 text-sm text-gray-700 dark:text-gray-300 truncate">
                                {key}
                              </label>
                            </div>
                          ))}
                    </div>
                    <div className="p-3 border-t border-gray-200 dark:border-gray-700 flex justify-between">
                      <button
                        className="text-xs text-gray-600 dark:text-gray-400 hover:underline"
                        onClick={() => {
                          const allColumns = {};
                          Object.keys(rebates[0])
                            .filter(key => key !== "_id")
                            .forEach(key => {
                              allColumns[key] = true;
                            });
                          setVisibleColumns(allColumns);
                        }}
                      >
                        Select All
                      </button>
                      <button
                        className="text-xs text-gray-600 dark:text-gray-400 hover:underline"
                        onClick={() => {
                          const minColumns = {};
                          const essentialColumns = ["Team Leader", "TL Status", "Rebate Amount", "Date Today"];
                          Object.keys(rebates[0])
                            .filter(key => key !== "_id")
                            .forEach(key => {
                              minColumns[key] = essentialColumns.includes(key);
                            });
                          setVisibleColumns(minColumns);
                        }}
                      >
                        Essential Only
                      </button>
                      <button
                        className="text-xs text-gray-600 dark:text-gray-400 hover:underline"
                        onClick={() => setShowColumnSelector(false)}
                      >
                        Close
                      </button>

                      <button
                        className="px-1.5 py-0.5 border border-gray-300 dark:border-gray-600 rounded text-[10px] bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center"
                        onClick={downloadCSV}
                      >
                        <svg className="w-3 h-3 mr-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v16h16V4H4zm4 4h8v8H8V8z"></path>
                        </svg>
                        Download
                      </button>



                    </div>


                  </div>
                )}
              </div>
              <div className="relative">
                {hasCheckedRows && (
                  <div className="flex space-x-2 ml-2 animate-fade-in">
                    <button
                      className="px-4 py-2 dark:bg-uasYellow bg-black dark:text-black dark:hover:bg-laryzeDarkGray dark:hover:text-white text-white font-medium text-sm rounded-lg hover:bg-uasYellow hover:text-black focus:ring-4 focus:ring-yellow-300 transition-all duration-300 transform flex items-center"
                      onClick={handleApproveSelected}
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      Approve Selected
                    </button>

                    <button
                      className="px-4 py-2 bg-red-600 text-white font-medium text-sm rounded-lg hover:bg-red-700 focus:ring-4 focus:ring-red-300 transition-all duration-300 transform flex items-center"
                      onClick={handleCancelSelected}
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      Dissaprove Selected
                    </button>

                    <button
                      className="px-4 py-2 bg-yellow-500 text-white font-medium text-sm rounded-lg hover:bg-yellow-600 focus:ring-4 focus:ring-yellow-300 transition-all duration-300 transform flex items-center"
                      onClick={handleMarkForApprovalSelected}
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                      </svg>
                      Mark as For Approval
                    </button>

                    <button
  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
  disabled={!hasCheckedRows}
  onClick={handleDeleteSelected}
>
  🗑️ Delete Selected
</button>

                  </div>

                  
                )}
              </div>

            </div>
          </div>



          {/* Table */}
          {loading ? (
            <div className={`bg-white dark:bg-laryzeDarkGray rounded-xl shadow-lg p-8 transition-all duration-700 transform ${getAnimationClass(4)}`}>
              <div className="flex flex-col items-center justify-center h-64">
                <div className="w-12 h-12 border-4 border-uasYellow border-t-transparent rounded-full animate-spin mb-4"></div>
                <p className="text-gray-500 dark:text-gray-400">Loading rebate data...</p>
              </div>
            </div>
          ) : filteredRebates.length === 0 ? (
            <div className={`bg-white dark:bg-laryzeDarkGray rounded-xl shadow-lg p-8 transition-all duration-700 transform ${getAnimationClass(4)}`}>
              <div className="flex flex-col items-center justify-center h-64 text-gray-400 dark:text-gray-500">
                <svg className="w-16 h-16 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                <p className="text-center italic">No rebate records found matching your filters.</p>
                <button
                  className="mt-4 px-4 py-2 bg-uasYellow text-laryzeDarkGray font-medium text-sm rounded-lg hover:bg-yellow-400 transition-all"
                  onClick={clearAllFilters}
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          ) : (
            <div ref={tableRef} className={`bg-white dark:bg-laryzeDarkGray rounded-xl shadow-lg transition-all duration-700 transform ${getAnimationClass(4)}`}>
              <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                <table className="min-w-full text-xs table-auto">
                  <thead className="bg-gray-100 dark:bg-laryzeDarkGray text-gray-700 dark:text-gray-200 sticky top-0 z-10">
                    <tr>
                      {/* Add this to the thead tr, before the other th elements */}
                      <th className="sticky left-0 z-10 bg-gray-100 dark:bg-laryzeDarkGray px-3 py-2 text-left font-semibold whitespace-nowrap border-b border-gray-300 dark:border-gray-700">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            className="w-4 h-4 text-uasYellow focus:ring-uasYellow rounded checked:accent-black dark:checked:accent-uasYellow dark:checked:text-black"
                            checked={allChecked}
                            onChange={handleSelectAll}
                            title="Select all eligible rebates on this page"
                          />
                        </div>
                      </th>

                      <th className="px-3 py-2 text-left font-semibold whitespace-nowrap border-b border-gray-300 dark:border-gray-700">
                        {/* Expand/Collapse All - Implementation would go here */}
                      </th>
                      {rebates.length > 0 &&
                        Object.keys(rebates[0])
                          .filter(key => key !== "_id" && visibleColumns[key])
                          .map(key => (
                            <th
                              key={key}
                              className="px-3 py-2 text-left font-semibold whitespace-nowrap border-b border-gray-300 dark:border-gray-700 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700"
                              onClick={() => requestSort(key)}
                            >
                              <div className="flex items-center">
                                {key.trim()}
                                {sortConfig.key === key ? (
                                  <span className="ml-1">
                                    {sortConfig.direction === 'ascending' ? (
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"></path>
                                      </svg>
                                    ) : (
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                      </svg>
                                    )}
                                  </span>
                                ) : (
                                  <span className="ml-1 text-gray-300 dark:text-gray-600">
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"></path>
                                    </svg>
                                  </span>
                                )}
                              </div>
                            </th>
                          ))}
                    </tr>
                  </thead>
                  <tbody>
                    {currentRecords.map((doc, index) => {
                      const isExpanded = expandedRows[index] || false;
                      const status = (doc["TL Status"] || "").toLowerCase().trim();
                      const isPending = status === "pending" || status === "for approval" || doc["TL Status"] === null;
                      const isApproved = status === "approved";
                      const isRejected = status === "cancelled";
                      
                      const isHighlighted = highlightedRow === index;

                      return (
                        <React.Fragment key={index}>
                          <tr
                            className={`
                          border-b border-gray-200 dark:border-gray-700 last:border-0 transition-colors duration-150
                          ${isHighlighted ? 'bg-yellow-50 dark:bg-yellow-900' : 'hover:bg-gray-50 dark:hover:bg-gray-700'}
                          ${isPending ? 'border-l-4 border-yellow-400 dark:border-yellow-600' : ''}
                          ${isApproved ? 'border-l-4 border-green-400 dark:border-green-600' : ''}
                          ${isRejected ? 'border-l-4 border-red-400 dark:border-red-600' : ''}
                        `}
                            onMouseEnter={() => setHighlightedRow(index)}
                            onMouseLeave={() => setHighlightedRow(null)}
                          >
                            {/* Add this to each row, before the other td elements */}
                            <td className="sticky left-0 z-[5] bg-white dark:bg-laryzeDarkGray px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">
                              {(doc["TL Status"] === null || (doc["TL Status"] || "").toLowerCase().trim() === "for approval" ||
                                (doc["TL Status"] || "").toLowerCase().trim() === "pending" ||
                                (doc["TL Status"] || "").toLowerCase().trim() === "") ? (
                                <input
                                  type="checkbox"
                                  className="w-4 h-4 text-uasYellow focus:ring-uasYellow rounded checked:accent-black dark:checked:accent-uasYellow dark:checked:text-black"
                                  checked={checkedRows[indexOfFirstRecord + index] || false}
                                  onChange={() => handleRowCheck(indexOfFirstRecord + index)}
                                />
                              ) : (
                                <span className="w-4 h-4 block"></span>
                              )}
                            </td>

                            <td className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">

                              <button
                                className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none"
                                onClick={() => toggleRowExpansion(index)}
                              >
                                {isExpanded ? (
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7"></path>
                                  </svg>
                                ) : (
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                  </svg>
                                )}
                              </button>
                            </td>
                            {Object.keys(rebates[0])
                              .filter(key => key !== "_id" && visibleColumns[key])
                              .map((key, i) => (
                                <td
                                  key={i}
                                  className="px-3 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300"
                                >
                                  {key === "Date Today"
                                    ? formatDate(doc[key])
                                    : key === "TL Status"
                                      ? (
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isPending ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                                          isApproved ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                            isRejected ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                                              'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                          }`}>
                                          {doc[key] || 'Pending'}
                                        </span>
                                      )
                                      : key === "Rebate Amount"
                                        ? `₱ ${parseFloat(doc[key] || 0).toFixed(2)}`
                                        : cleanValue(doc[key])
                                  }
                                </td>
                              ))}

                          </tr>
                          {isExpanded && (
                            <tr className="bg-gray-50 dark:bg-laryzeDarkGray">
                              <td colSpan={Object.keys(rebates[0]).filter(key => key !== "_id" && visibleColumns[key]).length + 2} className="p-4">
                                <div className="bg-gray-50 dark:bg-gray-900 text-gray-800 dark:text-white p-4 rounded-lg">
                                  {/* Rebate Summary Confirmation Header */}
                                  <h1 className="text-xl font-bold mb-4">Rebate Summary Confirmation</h1>

                                  {/* Rebate Calculator Section */}
                       

                              

                                  {/* CARE Format Section */}
                                  <div>
                                    <h2 className="text-lg font-semibold mb-4">CARE Format</h2>

                                    {/* C - Concern/Complaint */}
                                    <div className="mb-6">
                                      <h3 className="text-md font-medium mb-2">C - Concern/Complaint</h3>
                                      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                                        <p className="text-sm text-gray-800 dark:text-gray-200">{doc['Rebate Reason'] || 'N/A'}</p>
                                      </div>
                                    </div>

                                    {/* A - Action Taken */}
                                    <div className="mb-6">
                                      <h3 className="text-md font-medium mb-2">A - Action Taken and justification for the adjustment</h3>
                                      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                                      <ul className="list-disc pl-6 space-y-2 text-sm text-gray-800 dark:text-gray-200">
  <li>Details of the request: {doc['Request Details'] || 'N/A'}</li>
  <li>Reference Ticket Number (if any): {doc['Ticket Number'] || 'N/A'}</li>
  <li>Issue Start Date: {doc['Issue Start Date'] || 'N/A'}</li>
  <li>Issue End Date: {doc['Restoration Date'] || 'N/A'}</li>
  <li>Restoration Date: {doc['Restoration Date'] || 'N/A'}</li>
  <li>Bill Run: {doc['Bill Run'] || 'N/A'}</li>
  <li>Plan: {doc['Plan'] || 'N/A'}</li>
  <li>Requested Dates: {doc['Requested Dates'] || 'N/A'}</li>
  <li>Number of Days Requested: {doc['Number of Days Requested'] || 'N/A'}</li>
  <li><strong>Formula:</strong> {doc['Formula'] || 'N/A'}</li>
  <li>Estimated Rebate Amount: ₱{parseFloat(doc['Estimated Rebate Amount'] || 0).toFixed(2)}</li>
  <li>RFO: {doc['RFO'] || 'N/A'}</li>
</ul>

                                      </div>
                                    </div>

                                    {/* R - Resolution */}
                                    <div className="mb-6">
                                      <h3 className="text-md font-medium mb-2">R - Resolution</h3>
                                      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                                        <p className="text-sm text-gray-800 dark:text-gray-200">{doc['Resolution'] || 'N/A'}</p>
                                      </div>
                                    </div>

                                    {/* E - Expectation */}
                                    <div className="mb-6">
                                      <h3 className="text-md font-medium mb-2">E - Expectation</h3>
                                      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                                        <p className="text-sm text-gray-800 dark:text-gray-200">{doc['Expectation'] || 'N/A'}</p>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Additional Notes Section */}
                                  <div className="mb-6">
                                    <h3 className="text-md font-medium mb-2">Additional Notes</h3>
                                    <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
                                      <p className="text-sm text-gray-800 dark:text-gray-200">{doc['Notes'] || 'N/A'}</p>
                                    </div>
                                  </div>

                                  <div className="mt-4 flex justify-end">
                                    <button
                                      className="px-3 py-1 bg-laryzeDarkGray text-white text-xs rounded hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-600"
                                      onClick={() => toggleRowExpansion(index)}
                                    >
                                      Close
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>

                {/* Pagination Controls - Improved */}
                <div className="flex flex-col md:flex-row justify-between items-center p-4 bg-gray-50 dark:bg-laryzeDarkGray border-t border-gray-200 dark:border-gray-700 rounded-b-xl">
                  <div className="mb-4 md:mb-0 text-sm text-gray-500 dark:text-gray-400">
                    Showing {indexOfFirstRecord + 1}-{Math.min(indexOfLastRecord, filteredRebates.length)} of {filteredRebates.length} records
                  </div>

                  <div className="flex items-center">
                    <button
                      className="px-2 py-1.5 mr-1 bg-gray-200 dark:bg-gray-700 dark:text-gray-200 rounded-lg text-sm hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 transition-all duration-200"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7"></path>
                      </svg>
                    </button>

                    <button
                      className="px-2 py-2 mr-2 bg-gray-200 dark:bg-gray-700 dark:text-gray-200 rounded-lg text-sm hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 transition-all duration-200 flex items-center"
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
                      </svg>
                    </button>

                    <div className="flex items-center">
                      {/* Pagination page numbers */}
                      {[...Array(totalPages <= 5 ? totalPages : 5)].map((_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          // Show all pages if there are 5 or fewer
                          pageNum = i + 1;
                        } else {
                          // Show appropriate page numbers based on current page
                          if (currentPage <= 3) {
                            // Near the start
                            if (i < 4) {
                              pageNum = i + 1;
                            } else {
                              pageNum = totalPages;
                            }
                          } else if (currentPage >= totalPages - 2) {
                            // Near the end
                            if (i === 0) {
                              pageNum = 1;
                            } else {
                              pageNum = totalPages - (4 - i);
                            }
                          } else {
                            // In the middle
                            if (i === 0) {
                              pageNum = 1;
                            } else if (i === 4) {
                              pageNum = totalPages;
                            } else {
                              pageNum = currentPage + (i - 2);
                            }
                          }
                        }

                        // Handle ellipsis display
                        if ((totalPages > 5) && (
                          (i === 1 && pageNum !== 2) ||
                          (i === 3 && pageNum !== totalPages - 1)
                        )) {
                          return (
                            <span key={i} className="px-2 text-gray-500 dark:text-gray-400">
                              ...
                            </span>
                          );
                        }

                        return (
                          <button
                            key={i}
                            className={`w-8 h-8 mx-1 rounded-full focus:outline-none focus:ring-2 focus:ring-uasYellow ${currentPage === pageNum
                              ? 'bg-uasYellow text-laryzeDarkGray font-medium'
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                              }`}
                            onClick={() => setCurrentPage(pageNum)}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>

                    <button
                      className="px-2 py-2 ml-2 bg-gray-200 dark:bg-gray-700 dark:text-gray-200 rounded-lg text-sm hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 transition-all duration-200 flex items-center"
                      onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                    >
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                      </svg>
                    </button>

                    <button
                      className="px-2 py-1.5 ml-1 bg-gray-200 dark:bg-gray-700 dark:text-gray-200 rounded-lg text-sm hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50 transition-all duration-200"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5l7 7-7 7M5 5l7 7-7 7"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};



// Enhanced StatCard with icon and better animation
const StatCard = ({ label, value, icon, isLoaded, stage, requiredStage }) => (
  <div className={`bg-white dark:bg-laryzeDarkGray shadow-lg rounded-xl p-6 text-center transition-all duration-700 transform hover:-translate-y-2 hover:shadow-xl ${stage >= requiredStage ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
    <div className="flex justify-center mb-4">
      <div className="w-12 h-12 bg-uasYellow text-laryzeDarkGray rounded-full flex items-center justify-center text-xl">
        {icon}
      </div>
    </div>
    <p className="text-gray-500 dark:text-gray-400 text-sm uppercase tracking-wider mb-1">{label}</p>
    <p className="text-3xl font-bold text-gray-800 dark:text-white">{value}</p>
    <div className="w-16 h-1 bg-uasYellow mx-auto mt-4 rounded-full"></div>
  </div>
);

export default RebateViewer;